import {
  getSafeRedirectUrl,
  getWriteOnlyDomains,
  isAllowedDomain,
  isCashflowMarketplaceOrigin,
  parentDomain,
  sendPayload,
} from "common/constants/sso";
import {
  setToken,
  setRefreshToken,
  getToken,
  getCookieToken,
  getCookieRefreshToken,
  deleteToken,
  deleteRefreshToken,
  isIncognito,
  firebaseTokenKey,
  setFirebaseToken,
  deleteFirebaseToken,
  getFirebaseToken,
  add_cookie,
  getCookieFirebaseToken,
  getRefreshToken,
  tokenCookieKey,
  refreshTokenCookieKey,
  firebaseTokenCookieKey,
  delete_cookie,
  save,
  retrieve,
  deleteTokenForKey,
} from "lib/helpers";
import { clear, clearCredentialCookies } from "lib/helpers";
import { logout, resetState } from "features/Auth/Login/loginSlice";
import isNull from "lodash/isNull";
import { useDispatch } from "react-redux";
import { getStore } from "configureStore";
import { getCurrentUser } from "layouts/MainLayout/currentUserSlice";
import { tokenKey, refreshTokenKey } from "lib/helpers";

interface EventInterface {
  data: EventMessage;
  origin: string;
}
interface EventMessage {
  message: string;
  accessToken?: string;
  refreshToken?: string;
  firebaseToken?: string;
  redirectUrl?: string;
  key?: string;
  newValue?: string;
  isIncognito?: boolean;
  isAutoLogout?: boolean;
}
interface LocalStorageMessage {
  message: string;
  key?: string;
  newValue?: string;
}

interface LogoutMarker {
  at: number;
  accessJti: string | null;
}

let inMemoryLogoutMarker: LogoutMarker | null = null;

const useSSO = () => {
  const store = getStore();
  const dispatch = useDispatch<typeof store.dispatch>();
  const logoutMarkerKey = "SSO_LOGOUT_AT";
  const logoutMarkerMaxAgeMs = 30000;

  const clearLogoutMarker = () => {
    inMemoryLogoutMarker = null;
    deleteTokenForKey(logoutMarkerKey);
  };

  const getEffectiveAccessToken = () => getCookieToken() || getToken();
  const getEffectiveRefreshToken = () =>
    getCookieRefreshToken() || getRefreshToken();
  const getEffectiveFirebaseToken = () =>
    getCookieFirebaseToken() || getFirebaseToken();

  const decodeTokenPayload = (token?: string) => {
    if (!token) return null;

    try {
      const payload = token.split(".")[1];
      if (!payload) return null;

      const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
      const paddedPayload = normalizedPayload.padEnd(
        normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
        "=",
      );
      return JSON.parse(atob(paddedPayload));
    } catch {
      return null;
    }
  };

  const getLogoutMarker = () => {
    const rawMarker = retrieve(logoutMarkerKey);
    if (!rawMarker) return inMemoryLogoutMarker;

    try {
      const parsedMarker = JSON.parse(rawMarker);
      return {
        at: Number(parsedMarker.at),
        accessJti: parsedMarker.accessJti || null,
      };
    } catch {
      return {
        at: Number(rawMarker),
        accessJti: null,
      };
    }
  };

  const markLogout = () => {
    const currentToken = getEffectiveAccessToken();
    inMemoryLogoutMarker = {
      at: Date.now(),
      accessJti: decodeTokenPayload(currentToken)?.jti || null,
    };
    save(logoutMarkerKey, JSON.stringify(inMemoryLogoutMarker));
  };

  const hasRecentLogout = (incomingToken?: string) => {
    const marker = getLogoutMarker();
    if (!marker?.at || Number.isNaN(marker.at)) {
      clearLogoutMarker();
      return false;
    }

    if (Date.now() - marker.at > logoutMarkerMaxAgeMs) {
      clearLogoutMarker();
      return false;
    }

    const incomingPayload = decodeTokenPayload(incomingToken);
    if (incomingPayload?.jti && marker.accessJti) {
      if (incomingPayload.jti !== marker.accessJti) {
        clearLogoutMarker();
        return false;
      }
    } else if (
      typeof incomingPayload?.iat === "number" &&
      incomingPayload.iat * 1000 >= marker.at - 1000
    ) {
      clearLogoutMarker();
      return false;
    }

    return true;
  };

  const getIssuedAt = (token?: string) => {
    const issuedAt = decodeTokenPayload(token)?.iat;
    return typeof issuedAt === "number" ? issuedAt : null;
  };

  const shouldAdoptHostAuth = (incomingToken?: string) => {
    if (!incomingToken) return false;

    const currentToken = getEffectiveAccessToken();
    if (!currentToken) return true;
    if (incomingToken === currentToken) return false;

    const incomingIssuedAt = getIssuedAt(incomingToken);
    const currentIssuedAt = getIssuedAt(currentToken);
    if (!incomingIssuedAt || !currentIssuedAt) return false;

    return incomingIssuedAt >= currentIssuedAt;
  };

  const syncHostAuthIfNewer = (data: EventMessage) => {
    if (!shouldAdoptHostAuth(data.accessToken)) return;

    clearLogoutMarker();
    setToken(data.accessToken as string);
    add_cookie(tokenCookieKey, data.accessToken);

    if (data.refreshToken) {
      setRefreshToken(data.refreshToken);
      add_cookie(refreshTokenCookieKey, data.refreshToken);
    } else {
      deleteRefreshToken();
      delete_cookie(refreshTokenCookieKey);
    }

    if (data.firebaseToken) {
      setFirebaseToken(data.firebaseToken);
      add_cookie(firebaseTokenCookieKey, data.firebaseToken);
    } else {
      deleteFirebaseToken();
      delete_cookie(firebaseTokenCookieKey);
    }
  };

  const handleEvent = async (event: EventInterface) => {
    const targetOrigin = event.origin;
    switch (event.data.message) {
      case "LoadedIFrame":
        if (!isAllowedDomain(event.origin)) break;

        parentDomain.parentOrigin = event.origin;
        if (
          hasRecentLogout(event.data.accessToken) &&
          !getEffectiveAccessToken() &&
          !getEffectiveRefreshToken() &&
          (event.data.accessToken || event.data.refreshToken)
        ) {
          await destroySession(targetOrigin, true);
          logoutOrigin(targetOrigin);
          break;
        }
        if (getToken() && !getCookieToken() && !event.data.accessToken) {
          await destroySession(targetOrigin, true);
          logoutOrigin(targetOrigin);
          break;
        }
        syncHostAuthIfNewer(event.data);
        handleSafariOneWayCookies(event.data);
        if (
          getEffectiveAccessToken() ||
          getEffectiveRefreshToken()
        ) {
          const result = await dispatch(getCurrentUser());
          if (result.type.endsWith("/rejected")) {
            await destroySession(targetOrigin, true);
            logoutOrigin(targetOrigin);
            break;
          }
        }

        shareCookies(targetOrigin);
        break;

      case "Logout":
        if (!isAllowedDomain(event.origin)) break;

        await destroySession(targetOrigin, event.data.isAutoLogout);
        logoutOrigin(targetOrigin);
        break;

      case "RefreshToken":
        if (!isAllowedDomain(event.origin)) break;

        if (getEffectiveAccessToken()) {
          const result = await dispatch(getCurrentUser());
          if (result.type.endsWith("/rejected")) {
            await destroySession(targetOrigin, true);
            logoutOrigin(targetOrigin);
            break;
          }
        }
        if (!getEffectiveAccessToken() && !getEffectiveRefreshToken()) break;
        sendRefreshMessage(targetOrigin);
        break;

      case "SetLocalStorage":
        if (
          !isAllowedDomain(event.origin) &&
          !getWriteOnlyDomains().includes(event.origin)
        )
          break;

        handleStorageSet(event.data);
        break;
    }
  };

  //On safari, SSO is cleared when safari is closed, but localstorage can persist, this effectively logs the user out. If SSO is empty and host has keys, populate from host.
  const handleSafariOneWayCookies = (data: EventMessage) => {
    if (
      !getToken() &&
      !getCookieToken() &&
      !getEffectiveRefreshToken() &&
      !hasRecentLogout(data.accessToken)
    ) {
      if (data.accessToken) {
        setToken(data.accessToken);
        add_cookie(tokenCookieKey, data.accessToken);
      }
      if (data.refreshToken) {
        setRefreshToken(data.refreshToken);
        add_cookie(refreshTokenCookieKey, data.refreshToken);
      }
      if (data.firebaseToken) {
        setFirebaseToken(data.firebaseToken);
        add_cookie(firebaseTokenCookieKey, data.firebaseToken);
      }
    }
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key === tokenKey || event.key === firebaseTokenKey) {
      updateLocalStorage(parentDomain.parentOrigin, event.key, event.newValue);
    } else if (event.key === refreshTokenKey) {
      updateLocalStorage(parentDomain.parentOrigin, event.key, event.newValue);
      if (isNull(event.newValue)) {
        markLogout();
        logoutOrigin(parentDomain.parentOrigin);
      }
    }
  };

  const handleStorageSet = (message: LocalStorageMessage) => {
    if (message.key === tokenKey) {
      if (message.newValue === "") {
        markLogout();
        deleteToken();
        delete_cookie(tokenCookieKey);
      } else if (message.newValue) {
        clearLogoutMarker();
        setToken(message.newValue);
        add_cookie(tokenCookieKey, message.newValue);
      }
    } else if (message.key === refreshTokenKey) {
      if (message.newValue === "") {
        markLogout();
        deleteRefreshToken();
        delete_cookie(refreshTokenCookieKey);
      } else if (message.newValue) {
        clearLogoutMarker();
        setRefreshToken(message.newValue);
        add_cookie(refreshTokenCookieKey, message.newValue);
      }
    } else if (message.key === firebaseTokenKey) {
      if (message.newValue === "") {
        deleteFirebaseToken();
        delete_cookie(firebaseTokenCookieKey);
      } else if (message.newValue) {
        setFirebaseToken(message.newValue);
        add_cookie("__firebase_token", message.newValue);
      }
    }
  };

  const shareCookies = (targetOrigin = parentDomain.parentOrigin) => {
    const cookieToken = getEffectiveAccessToken();
    const refreshToken = getEffectiveRefreshToken();
    const firebaseToken = getEffectiveFirebaseToken();
    const incognito = isIncognito();
    const message = {
      message: "SharingCookies",
      accessToken: cookieToken,
      refreshToken: refreshToken,
      firebaseToken: firebaseToken,
      isIncognito: incognito,
    };
    sendPayload(message, targetOrigin);
  };

  const updateLocalStorage = (
    sendDomain: string,
    key: string,
    newValue: string | null,
  ) => {
    const message = {
      message: "UpdateLocalStorage",
      key: key,
      newValue: newValue,
    };
    if (isAllowedDomain(sendDomain)) {
      sendPayload(message, sendDomain);
    }
  };

  const destroySession = async (
    targetOrigin = parentDomain.parentOrigin,
    isAutoLogout = false,
  ) => {
    markLogout();
    await dispatch(logout(isAutoLogout));
    dispatch(resetState());
    clearCredentialCookies();
    clear();
    shareCookies(targetOrigin);
  };

  const logoutOrigin = (sendDomain: string) => {
    const message = {
      message: "LogoutSuccess",
    };
    if (isAllowedDomain(sendDomain)) {
      sendPayload(message, sendDomain);
    }
  };

  const sendLoginPayload = (loginPayload: any) => {
    clearLogoutMarker();
    sendPayload({
      ...loginPayload,
      redirectUrl: getSafeRedirectUrl(loginPayload?.redirectUrl),
    });
  };

  const isCashflowMarketplace = () => {
    return isCashflowMarketplaceOrigin(parentDomain.parentOrigin);
  };

  const sendRefreshMessage = (targetOrigin = parentDomain.parentOrigin) => {
    const refreshToken = getEffectiveRefreshToken();
    const accessToken = getEffectiveAccessToken();
    if (!accessToken && !refreshToken) return;

    const payload: EventMessage = {
      message: "RefreshReturn",
      accessToken: accessToken,
      refreshToken: refreshToken,
    };
    sendPayload(payload, targetOrigin);
  };

  return {
    handleEvent,
    handleStorage,
    isCashflowMarketplace,
    sendLoginPayload,
    sendPayload,
  };
};

export default useSSO;
